import React, { useState, useEffect, useMemo, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ChevronRight, ChevronDown, Eye, Edit3, Trash2 } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';

const CategoryList = () => {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const canAdd = hasPermission('category', 'add');
  const canEdit = hasPermission('category', 'edit');
  const canDelete = hasPermission('category', 'delete');
  const canView = hasPermission('category', 'view');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [canEditTranslations, setCanEditTranslations] = useState(false);

  // Build parent→children index (O(1) child lookup)
  const childrenMap = useMemo(() => {
    const map = {};
    categories.forEach(c => {
      const pid = c.parent_id ?? '__root__';
      (map[pid] ??= []).push(c);
    });
    return map;
  }, [categories]);

  // Build id→parent_id index (O(1) ancestor tracing)
  const parentMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.parent_id; });
    return map;
  }, [categories]);

  // Build id→category index (O(1) category lookup)
  const idMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c; });
    return map;
  }, [categories]);

  // Deferred search: keeps UI responsive during typing
  const deferredSearch = useDeferredValue(searchQuery);

  // Memoized search matching (runs on deferred value, not every keystroke)
  const searchMatchIds = useMemo(() => {
    if (!deferredSearch.trim()) return null;
    const q = deferredSearch.toLowerCase();
    const matching = new Set();
    categories.forEach(c => {
      const def = (c.default_name || '').toLowerCase();
      const zh = ((c.labels && c.labels.zh) || '').toLowerCase();
      const en = ((c.labels && c.labels.en) || '').toLowerCase();
      if (def.includes(q) || zh.includes(q) || en.includes(q)) {
        matching.add(c.id);
        let pid = c.parent_id;
        while (pid) {
          matching.add(pid);
          pid = parentMap[pid] ?? null;
        }
      }
    });
    return matching;
  }, [categories, deferredSearch, parentMap]);

  useEffect(() => {
    setLoading(true);
    fetch('/api/categories')
      .then(res => res.json())
      .then(res => {
        if (res && res.status === 'success' && res.data) {
          setCategories(res.data);
          if (res.meta && res.meta.can_edit_translations) {
            setCanEditTranslations(true);
          }
        } else {
          setCategories([]);
        }
        setLoading(false);
      })
      .catch(() => {
        setCategories([]);
        setLoading(false);
      });
  }, []);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getLocalizedName = (cat) => {
    if (lang && cat.labels && cat.labels[lang]) return cat.labels[lang];
    return cat.default_name || '';
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete category "${name}" and all its associations?`)) return;
    try {
      const res = await fetch(`/api/categories/${id}/delete`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        setCategories(prev => prev.filter(c => c.id !== id));
        notify(`Category "${name}" deleted`, 'success');
      } else {
        notify(`Delete failed: ${data.message}`, 'error');
      }
    } catch (err) {
      notify('Delete failed: ' + err.message, 'error');
    }
  };

  // Render tree rows: only show matching + their ancestors when searching
  const renderCategories = (parentId = null, level = 0) => {
    if (!categories) return null;
    const isSearching = !!(searchQuery && searchQuery.trim());

    const children = (childrenMap[parentId ?? '__root__'] || [])
      .filter(c => !isSearching || (searchMatchIds && searchMatchIds.has(c.id)));

    return children.map(c => {
      const hasKids = (childrenMap[c.id]?.length ?? 0) > 0;
      const isExpanded = expanded[c.id] || false;
      const autoExpand = isSearching && hasKids && (childrenMap[c.id] || []).some(ch =>
        searchMatchIds && searchMatchIds.has(ch.id)
      );
      const showChildren = isExpanded || autoExpand;

      return (
        <div key={c.id} className="flex flex-col">
          <div
            className={`flex items-center justify-between p-3 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors ${level === 0 ? 'font-semibold' : ''}`}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
            onClick={() => toggleExpand(c.id)}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-5 shrink-0">
                {hasKids ? (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span className="text-slate-300">•</span>}
              </div>
              <span className="truncate">{getLocalizedName(c)}</span>
              {c.is_system == 1 && (
                <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700">
                  SYSTEM
                </span>
              )}
            </div>
            <div className="flex gap-2 items-center" onClick={e => e.stopPropagation()}>
              {/* View - always active */}
              {canView && (
                <button
                  onClick={() => navigate(`/categories/view/${c.id}`)}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  title="View"
                >
                  <Eye size={16} />
                </button>
              )}
              {/* Edit */}
              {canEdit && (
                c.is_system == 1 ? (
                  canEditTranslations ? (
                    <button
                      onClick={() => navigate(`/categories/edit/${c.id}`)}
                      className="p-1.5 rounded-lg text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                      title="Edit (translatable fields only)"
                    >
                      <Edit3 size={16} />
                    </button>
                  ) : (
                    <span className="p-1.5 text-slate-300 dark:text-slate-600 cursor-not-allowed inline-block" title="System category - read only">
                      <Edit3 size={16} />
                    </span>
                  )
                ) : (
                  <button
                    onClick={() => navigate(`/categories/edit/${c.id}`)}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Edit"
                  >
                    <Edit3 size={16} />
                  </button>
                )
              )}
              {/* Delete */}
              {canDelete && (
                c.is_system == 1 ? (
                  <span className="p-1.5 text-slate-300 dark:text-slate-600 cursor-not-allowed inline-block" title="System category - cannot delete">
                    <Trash2 size={16} />
                  </span>
                ) : (
                  <button
                    onClick={() => handleDelete(c.id, getLocalizedName(c))}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              ))}
            </div>
          </div>
          {showChildren && renderCategories(c.id, level + 1)}
        </div>
      );
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <h1 className="text-2xl font-bold">{t('categories.title')}</h1>
        <div className="flex gap-3">
          {canAdd && (
            <button
              onClick={() => navigate('/categories/new')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              {t('common.add')}
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          placeholder="Search categories across all languages..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="divide-y divide-slate-100 dark:divide-slate-700">
          {loading ? (
            /* Loading skeleton */
            <div className="p-8 flex flex-col items-center gap-3">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              <span className="text-sm text-slate-400">{t('common.loading')}</span>
            </div>
          ) : categories.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No categories found</div>
          ) : (
            renderCategories()
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryList;
